import { ViinexSourceStorage } from '@/types/viinex';
import { viinexClient } from '@/services/viinex-client';
import { ViinexStorageData } from '@/types/streaming';
import Config from '@/config';
import {
    parseViinexTimeBoundaries,
    parseViinexTimelineIntervals,
} from '@/utils/viinex';
import { buildWebRTCPlayRequest } from '@/utils/webrtc';

class ViinexService {
    get baseUrl(): string {
        return Config.VITE_VIINEX_URL;
    }

    private get endpoints() {
        return {
            base: () => this.baseUrl,
            service: (serviceName: string) => `${this.baseUrl}/${serviceName}`,
            storage: (storageService: string, sourceId: string) =>
                `${this.baseUrl}/${storageService}/${sourceId}`,
            hlsStream: (
                storageService: string,
                sourceId: string,
                beginMilliseconds: number,
                endMilliseconds: number,
            ) =>
                `${this.baseUrl}/${storageService}/${sourceId}/stream.m3u8?begin=${beginMilliseconds}&end=${endMilliseconds}`,
            webrtcSession: (webRtcService: string, sessionId: string) =>
                `${this.baseUrl}/${webRtcService}/${sessionId}`,
            webrtcAnswer: (webRtcService: string, sessionId: string) =>
                `${this.baseUrl}/${webRtcService}/${sessionId}/answer`,
        } as const;
    }

    getCameraSnapshotUrl(
        sourceId: string,
        {
            width,
            height,
            storageService,
            timestamp,
        }: {
            width?: number;
            height?: number;
            storageService?: string;
            timestamp?: number;
        } = {},
    ): string {
        const prefix = storageService
            ? `${storageService}/${sourceId}`
            : sourceId;
        const url = `${this.baseUrl}/${prefix}/snapshot`;

        const params = new URLSearchParams();
        if (timestamp != null) params.set('timestamp', String(timestamp));
        if (width != null) params.set('width', String(width));
        if (height != null) params.set('height', String(height));

        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
    }

    async fetchStorageData(sourceId: string): Promise<ViinexStorageData> {
        const storageService = await this.getVideoStorageServiceName(sourceId);

        const sourceStorage = await viinexClient.get<ViinexSourceStorage>(
            this.endpoints.storage(storageService, sourceId),
        );

        const boundaries = parseViinexTimeBoundaries(
            sourceStorage.timeBoundaries,
        );
        if (!boundaries) {
            throw new Error('Invalid time boundaries');
        }

        const intervals = parseViinexTimelineIntervals(
            sourceStorage.timeline,
            boundaries.earliestSeconds,
            boundaries.latestSeconds,
        );

        return {
            storageService,
            viinexBaseUrl: this.baseUrl,
            boundaries,
            intervals,
            viinexSourceId: sourceId,
        };
    }

    buildHlsUrl(
        storageService: string,
        viinexSourceId: string,
        beginMilliseconds: number,
        endMilliseconds: number,
    ): string {
        return this.endpoints.hlsStream(
            storageService,
            viinexSourceId,
            beginMilliseconds,
            endMilliseconds,
        );
    }

    async fetchWebRTCOffer(
        webRtcService: string,
        sessionId: string,
        sourceId: string,
    ): Promise<string> {
        const url = this.endpoints.webrtcSession(webRtcService, sessionId);

        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildWebRTCPlayRequest(sourceId)),
        });

        if (!response.ok) {
            throw new Error(
                `Failed to get WebRTC offer: ${response.statusText}`,
            );
        }

        return response.text();
    }

    async sendWebRTCAnswer(
        webRtcService: string,
        sessionId: string,
        sdp: string,
    ): Promise<void> {
        const url = this.endpoints.webrtcAnswer(webRtcService, sessionId);

        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/sdp' },
            body: sdp,
        });

        if (!response.ok) {
            throw new Error(
                `Failed to send WebRTC answer: ${response.statusText}`,
            );
        }
    }

    async deleteWebRTCSession(
        webRtcService: string,
        sessionId: string,
    ): Promise<void> {
        const url = this.endpoints.webrtcSession(webRtcService, sessionId);

        try {
            await fetch(url, { method: 'DELETE' });
        } catch (err) {
            console.error('Failed to delete WebRTC session:', err);
        }
    }

    private async discoverViinexServiceByType<T>(
        serviceType: string,
        predicate: (summary: T) => Promise<boolean>,
    ): Promise<string> {
        const viinexServices = await viinexClient.get<[string, string][]>(
            this.endpoints.base(),
        );

        const candidateServices = viinexServices.filter(
            ([type]) => type === serviceType,
        );

        for (const [, serviceName] of candidateServices) {
            try {
                const summary = await viinexClient.get<T>(
                    this.endpoints.service(serviceName),
                );
                if (await predicate(summary)) {
                    return serviceName;
                }
            } catch {
                continue;
            }
        }

        throw new Error(`No ${serviceType} service found matching criteria`);
    }

    async getVideoStorageServiceName(sourceId: string): Promise<string> {
        const normalizedSourceId = sourceId.replace(/[-_]/g, '').toLowerCase();

        return await this.discoverViinexServiceByType<{
            contexts: Record<string, unknown>;
        }>('VideoStorage', async (summary) => {
            const contextKeys = Object.keys(summary.contexts || {});
            return contextKeys.some(
                (key) => key.toLowerCase() === normalizedSourceId,
            );
        });
    }

    async getWebRtcServiceName(sourceId: string): Promise<string> {
        return await this.discoverViinexServiceByType<{ live: string[] }>(
            'WebRTC',
            async (summary) => summary.live.includes(sourceId),
        );
    }

    async getCameraLiveStatus(sourceId: string): Promise<{
        live: boolean;
        timestamp?: number;
        error?: string;
    }> {
        try {
            await this.getWebRtcServiceName(sourceId);
            return {
                live: true,
                timestamp: Date.now(),
            };
        } catch {
            return { live: false };
        }
    }
}

export default new ViinexService();
